import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Target, Calendar, Plus, Search, Eye, Trash2, Edit3,
  AlertTriangle, CheckCircle, ArrowRight, Upload, FileText, MessageSquare,
  BarChart3, DollarSign, Paperclip, RefreshCw
} from 'lucide-react';
import {
  getCRMContacts, getCRMContactSummary,
  createCRMContact, updateCRMContact, deleteCRMContact,
  getCRMDeals, createCRMDeal, updateCRMDeal, updateCRMDealStage, deleteCRMDeal,
  getCRMPipeline, getCRMFollowups, createCRMFollowup,
  completeCRMFollowup, deleteCRMFollowup, getCRMDashboard,
  getCRMDealAttachments, uploadCRMDealAttachment, deleteCRMDealAttachment,
  getCRMDealNotes, createCRMDealNote, getCRMRecentActivity
} from '../api';
import MOMTab from './MOMTab';

const formatRupiah = (v) => 'Rp ' + (Number(v) || 0).toLocaleString('id-ID');

const STAGES = [
  { key: 'prospek', label: 'Prospek', color: '#6366f1', bg: '#eef2ff' },
  { key: 'hubungi', label: 'Hubungi', color: '#f59e0b', bg: '#fffbeb' },
  { key: 'penawaran', label: 'Penawaran', color: '#3b82f6', bg: '#eff6ff' },
  { key: 'negosiasi', label: 'Negosiasi', color: '#8b5cf6', bg: '#f5f3ff' },
  { key: 'deal', label: 'Deal', color: '#10b981', bg: '#ecfdf5' },
  { key: 'lose', label: 'Lose', color: '#ef4444', bg: '#fef2f2' },
  { key: 'selesai', label: 'Selesai', color: '#059669', bg: '#ecfdf5' },
  { key: 'menang', label: 'Menang', color: '#10b981', bg: '#ecfdf5' },
  { key: 'kalah', label: 'Kalah', color: '#ef4444', bg: '#fef2f2' },
];

const FOLLOWUP_TIPE = [
  { value: 'telepon', label: '📞 Telepon' },
  { value: 'email', label: '📧 Email' },
  { value: 'kunjungan', label: '🚗 Kunjungan' },
  { value: 'meeting', label: '🤝 Meeting' },
  { value: 'lainnya', label: '📝 Lainnya' },
];

// ============ MAIN ============
export default function CRM() {
  const [tab, setTab] = useState('dashboard');
  const tabs = [
    { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { key: 'contacts', label: 'Contacts', icon: Users },
    { key: 'pipeline', label: 'Pipeline', icon: Target },
    { key: 'followups', label: 'Follow-up', icon: Calendar },
    { key: 'mom', label: 'MOM', icon: FileText },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', margin: 0 }}>🎯 CRM</h1>
        <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0 0' }}>Customer Relationship Management</p>
      </div>
      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #e5e7eb', marginBottom: 20 }}>
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px',
                border: 'none', background: tab === t.key ? '#6366f1' : 'transparent',
                color: tab === t.key ? '#fff' : '#6b7280', borderRadius: '8px 8px 0 0',
                fontWeight: 600, fontSize: 14, cursor: 'pointer'
              }}>
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === 'dashboard' && <DashboardTab />}
      {tab === 'contacts' && <ContactsTab />}
      {tab === 'pipeline' && <PipelineTab />}
      {tab === 'followups' && <FollowupsTab />}
      {tab === 'mom' && <MOMTab />}
    </div>
  );
}

// ============ DASHBOARD ============
function DashboardTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getCRMDashboard();
      setStats(data);
    } catch (e) {
      console.error('CRM Dashboard error:', e);
      setError(e.message || 'Gagal memuat dashboard');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: '#6b7280', fontSize: 14 }}>Memuat...</div>;
  if (error) return <div style={{ textAlign: 'center', padding: 40, color: '#ef4444', fontSize: 14 }}>❌ {error}</div>;
  if (!stats) return null;

  const cards = [
    { label: 'Contacts', value: stats.total_contacts, icon: Users, color: '#6366f1' },
    { label: 'Deals Aktif', value: stats.total_deals, icon: Target, color: '#f59e0b' },
    { label: 'Pipeline', value: formatRupiah(stats.pipeline_value), icon: DollarSign, color: '#10b981' },
    { label: 'Follow-up Hari Ini', value: stats.followup_today, icon: Calendar, color: '#3b82f6' },
    { label: 'Terlewat', value: stats.followup_overdue, icon: AlertTriangle, color: '#ef4444' },
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14, marginBottom: 24 }}>
        {cards.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} style={{ background: '#fff', borderRadius: 12, padding: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', borderLeft: `4px solid ${s.color}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Icon size={22} color={s.color} />
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#1e293b' }}>{s.value}</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>{s.label}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Pipeline Summary */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#374151', marginBottom: 14 }}>📊 Pipeline Summary</h3>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {STAGES.filter(s => s.key !== 'menang' && s.key !== 'kalah').map(s => {
            const sd = (stats.pipeline_by_stage || []).find(x => x.stage === s.key);
            return (
              <div key={s.key} style={{ flex: '1 1 100px', background: s.bg, borderRadius: 10, padding: 14, textAlign: 'center', border: `1px solid ${s.color}25` }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{sd?.count || 0}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: s.color }}>{s.label}</div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>{formatRupiah(sd?.value || 0)}</div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Recent Deals */}
      {stats.recent_deals?.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#374151', marginBottom: 14 }}>🕐 Deals Terbaru</h3>
          {stats.recent_deals.map(d => {
            const stg = STAGES.find(s => s.key === d.stage) || STAGES[0];
            return (
              <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{d.judul}</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>{d.contact_nama}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, color: '#6b7280' }}>{formatRupiah(d.nilai_estimasi)}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 6, background: stg.bg, color: stg.color }}>{stg.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============ CONTACTS ============
function ContactsTab() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterJenis, setFilterJenis] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getCRMContacts({ search, jenis: filterJenis });
      setContacts(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [search, filterJenis]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus contact ini?')) return;
    try { await deleteCRMContact(id); load(); } catch (e) { alert('Gagal: ' + e.message); }
  };

  const handleSave = async (form) => {
    try {
      if (editData) await updateCRMContact(editData.id, form);
      else await createCRMContact(form);
      setShowModal(false); setEditData(null); load();
    } catch (e) { alert('Gagal: ' + e.message); }
  };

  const openDetail = async (id) => {
    try { const { data } = await getCRMContactSummary(id); setDetail(data); } catch (e) { alert('Gagal'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 200px' }}>
          <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari..."
            style={{ width: '100%', padding: '9px 12px 9px 34px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }} />
        </div>
        <select value={filterJenis} onChange={e => setFilterJenis(e.target.value)}
          style={{ padding: '9px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}>
          <option value="">Semua Jenis</option>
          <option value="Perorangan">Perorangan</option>
          <option value="Koperasi">Koperasi</option>
          <option value="Karoseri">Karoseri</option>
          <option value="Perusahaan">Perusahaan</option>
          <option value="Pemerintah">Pemerintah</option>
          <option value="Supplier">Supplier</option>
                <option value="Dealer">Dealer</option>
        </select>
        <button onClick={() => { setEditData(null); setShowModal(true); }}
          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '9px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          <Plus size={15} /> Tambah
        </button>
      </div>
      <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Nama</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Perusahaan</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>PIC</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Tipe</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: 28, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Memuat...</td></tr>
            ) : contacts.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 28, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Tidak ada data</td></tr>
            ) : contacts.map(c => (
              <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '10px 14px', fontWeight: 600 }}>{c.nama}</td>
                <td style={{ padding: '10px 14px', color: '#6b7280' }}>{c.perusahaan || '-'}</td>
                <td style={{ padding: '10px 14px', color: '#6b7280' }}>{c.pic || '-'}</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
                    background: c.jenis === 'Perusahaan' ? '#ecfdf5' : c.jenis === 'Perorangan' ? '#eff6ff' : c.jenis === 'Koperasi' ? '#fef3c7' : c.jenis === 'Karoseri' ? '#ede9fe' : c.jenis === 'Pemerintah' ? '#fef2f2' : '#f0f9ff',
                    color: c.jenis === 'Perusahaan' ? '#065f46' : c.jenis === 'Perorangan' ? '#1e40af' : c.jenis === 'Koperasi' ? '#92400e' : c.jenis === 'Karoseri' ? '#5b21b6' : c.jenis === 'Pemerintah' ? '#dc2626' : '#0369a1'
                  }}>{c.jenis}</span>
                </td>
                <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                    <button onClick={() => openDetail(c.id)} style={{ background: '#eff6ff', border: 'none', borderRadius: 6, padding: 6, cursor: 'pointer' }}><Eye size={15} color="#3b82f6" /></button>
                    <button onClick={() => { setEditData(c); setShowModal(true); }} style={{ background: '#fef3c7', border: 'none', borderRadius: 6, padding: 6, cursor: 'pointer' }}><Edit3 size={15} color="#f59e0b" /></button>
                    <button onClick={() => handleDelete(c.id)} style={{ background: '#fef2f2', border: 'none', borderRadius: 6, padding: 6, cursor: 'pointer' }}><Trash2 size={15} color="#ef4444" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showModal && <ContactModal editData={editData} onClose={() => { setShowModal(false); setEditData(null); }} onSave={handleSave} />}
      {detail && <ContactDetailModal data={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function ContactModal({ editData, onClose, onSave }) {
  const [form, setForm] = useState({
    nama: editData?.nama || '', perusahaan: editData?.perusahaan || '', pic: editData?.pic || '',
    no_hp: editData?.no_hp || '', email: editData?.email || '', alamat: editData?.alamat || '',
    jenis: editData?.jenis || 'Perorangan', sumber: editData?.sumber || 'manual',
    status: editData?.status || 'aktif', catatan: editData?.catatan || '',
  });
  const handleSubmit = (e) => { e.preventDefault(); if (!form.nama.trim()) return alert('Nama wajib diisi'); onSave(form); };
  const inputS = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 };
  const labelS = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{editData ? 'Edit' : 'Tambah'} Contact</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: 18 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={labelS}>Nama *</label><input style={inputS} value={form.nama} onChange={e => setForm({ ...form, nama: e.target.value })} /></div>
            <div><label style={labelS}>Perusahaan</label><input style={inputS} value={form.perusahaan} onChange={e => setForm({ ...form, perusahaan: e.target.value })} /></div>
            <div><label style={labelS}>PIC</label><input style={inputS} value={form.pic} onChange={e => setForm({ ...form, pic: e.target.value })} /></div>
            <div><label style={labelS}>No HP</label><input style={inputS} value={form.no_hp} onChange={e => setForm({ ...form, no_hp: e.target.value })} /></div>
            <div><label style={labelS}>Email</label><input style={inputS} type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><label style={labelS}>Jenis</label>
              <select style={inputS} value={form.jenis} onChange={e => setForm({ ...form, jenis: e.target.value })}>
                <option value="Perorangan">Perorangan</option><option value="Koperasi">Koperasi</option><option value="Karoseri">Karoseri</option><option value="Perusahaan">Perusahaan</option><option value="Pemerintah">Pemerintah</option><option value="Supplier">Supplier</option>
                <option value="Dealer">Dealer</option>
              </select></div>
          </div>
          <div style={{ marginTop: 10 }}><label style={labelS}>Catatan</label><textarea style={{ ...inputS, height: 60 }} value={form.catatan} onChange={e => setForm({ ...form, catatan: e.target.value })} /></div>
          <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, cursor: 'pointer', background: '#fff' }}>Batal</button>
            <button type="submit" style={{ padding: '8px 16px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, background: '#6366f1', color: '#fff', cursor: 'pointer' }}>Simpan</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ContactDetailModal({ data, onClose }) {
  const { contact, deals, logs } = data;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>👤 {contact.nama}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
            {[['Perusahaan', contact.perusahaan], ['PIC', contact.pic], ['No HP', contact.no_hp], ['Email', contact.email], ['Jenis', contact.jenis], ['Sumber', contact.sumber]].map(([l, v]) => (
              <div key={l}><div style={{ fontSize: 12, color: '#6b7280' }}>{l}</div><div style={{ fontSize: 14, fontWeight: 600 }}>{v || '-'}</div></div>
            ))}
          </div>
          <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>🎯 Deals ({deals.length})</h4>
          {deals.length === 0 ? <p style={{ fontSize: 13, color: '#9ca3af' }}>Belum ada deals</p> : deals.map(d => {
            const stg = STAGES.find(s => s.key === d.stage) || STAGES[0];
            return (
              <div key={d.id} style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: 8, marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div><div style={{ fontSize: 14, fontWeight: 600 }}>{d.judul}</div><div style={{ fontSize: 13, color: '#6b7280' }}>{formatRupiah(d.nilai_estimasi)}</div></div>
                <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 6, background: stg.bg, color: stg.color }}>{stg.label}</span>
              </div>
            );
          })}
          <h4 style={{ fontSize: 14, fontWeight: 600, marginTop: 14, marginBottom: 8 }}>📝 Komunikasi ({logs.length})</h4>
          {logs.length === 0 ? <p style={{ fontSize: 13, color: '#9ca3af' }}>Belum ada catatan</p> : logs.map(l => (
            <div key={l.id} style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: 8, marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{l.subjek || l.tipe}</span>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>{l.created_at}</span>
              </div>
              {l.isi && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{l.isi}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============ PIPELINE (Redesigned) ============
function PipelineTab() {
  const [pipeline, setPipeline] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editDeal, setEditDeal] = useState(null);
  const [viewDeal, setViewDeal] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [upcomingFollowups, setUpcomingFollowups] = useState([]);
  const today = new Date();
  const defaultDari = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-01`;
  const defaultSampai = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(new Date(today.getFullYear(), today.getMonth()+1, 0).getDate()).padStart(2,'0')}`;
  const [filterDari, setFilterDari] = useState(defaultDari);
  const [filterSampai, setFilterSampai] = useState(defaultSampai);


  const filterByDate = (deals) => {
    if (!filterDari && !filterSampai) return deals;
    return deals.filter(d => {
      const tgl = d.created_at?.substring(0, 10) || '';
      if (filterDari && tgl < filterDari) return false;
      if (filterSampai && tgl > filterSampai) return false;
      return true;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cRes, actRes, fuRes] = await Promise.all([
        getCRMPipeline(),
        getCRMContacts(),
        getCRMRecentActivity(),
        getCRMFollowups({ date: 'all' }),
      ]);
      setPipeline(pRes.data);
      setContacts(cRes.data);
      setActivities(actRes.data || []);
      const now = new Date();
      const upcoming = (fuRes.data || []).filter(f =>
        f.status === 'terjadwal' && new Date(f.jadwal) >= now
      ).slice(0, 10);
      setUpcomingFollowups(upcoming);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleMove = async (dealId, newStage) => {
    try { await updateCRMDealStage(dealId, newStage); load(); } catch (e) { alert('Gagal: ' + e.message); }
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteDealId, setDeleteDealId] = useState(null);
  const [deleteKeterangan, setDeleteKeterangan] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDelete = async (dealId) => {
    setDeleteDealId(dealId);
    setDeleteKeterangan('');
    setDeletePassword('');
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteKeterangan.trim()) {
      setDeleteError('Keterangan wajib diisi');
      return;
    }
    if (!deletePassword.trim()) {
      setDeleteError('Password wajib diisi');
      return;
    }
    setDeleteLoading(true);
    setDeleteError('');
    try {
      // Password dikirim ke backend untuk diverifikasi di server
      // (tidak perlu panggil /api/settings yang hanya untuk admin)
      await deleteCRMDeal(deleteDealId, deleteKeterangan, deletePassword);
      setShowDeleteModal(false);
      load();
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Terjadi kesalahan';
      setDeleteError(msg);
    }
    setDeleteLoading(false);
  };

  const handleEditSave = async (form) => {
    try {
      await updateCRMDeal(editDeal.id, { ...form, stage: editDeal.stage, contact_id: parseInt(form.contact_id), nilai_estimasi: parseFloat(form.nilai_estimasi) || 0 });
      setEditDeal(null); load();
    } catch (e) { alert('Gagal: ' + e.message); }
  };



  // 6 kolom pipeline: Prospek, Hubungi, Penawaran, Negosiasi, Deal, Lose
  const kanbanStages = STAGES.filter(s => ['prospek', 'hubungi', 'penawaran', 'negosiasi', 'deal', 'lose'].includes(s.key));

  // Hitung statistik ringkasan
  const allDeals = Object.values(pipeline).flat();
  const wonDeals = allDeals.filter(d => ['deal', 'menang'].includes(d.stage));
  const lostDeals = allDeals.filter(d => ['lose', 'kalah'].includes(d.stage));
  const totalDealCount = allDeals.length;
  const totalNilai = allDeals.reduce((a, d) => a + (d.nilai_estimasi || 0), 0);
  const dealMenangCount = wonDeals.length;
  const dealLoseCount = lostDeals.length;

  // Pie chart percentages
  const totalResult = dealMenangCount + dealLoseCount;
  const pctMenang = totalResult > 0 ? Math.round((dealMenangCount / totalResult) * 100) : 0;
  const pctLose = totalResult > 0 ? 100 - pctMenang : 0;

  if (loading) return <div style={{ textAlign: 'center', padding: 30, color: '#6b7280', fontSize: 14 }}>Memuat pipeline...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0 }}>📋 Sales Pipeline</h3>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#6b7280' }}>📅</span>
          <input type="date" value={filterDari} onChange={e => setFilterDari(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
          <span style={{ fontSize: 13, color: '#6b7280' }}>s/d</span>
          <input type="date" value={filterSampai} onChange={e => setFilterSampai(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
          {(filterDari || filterSampai) && (
            <button onClick={() => { setFilterDari(''); setFilterSampai(''); }}
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, background: '#fff', cursor: 'pointer' }}>Reset</button>
          )}
          <button onClick={() => setShowAdd(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginLeft: 4 }}>
            <Plus size={15} /> Tambah Deal
          </button>
        </div>
      </div>

      {/* ===== ROW 1: PIPELINE KANBAN — 6 Kolom ===== */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10, marginBottom: 20 }}>
        {/* === DEAL STAGES === */}
        {kanbanStages.map(s => {
          const deals = filterByDate(pipeline[s.key] || []);
          // Merge old stages for backward compat
          const extraDeals = s.key === 'deal'
            ? filterByDate([...(pipeline['menang'] || []), ...(pipeline['selesai'] || [])])
            : s.key === 'lose'
            ? filterByDate(pipeline['kalah'] || [])
            : [];
          const allStageDeals = [...deals, ...extraDeals];
          return (
            <div key={s.key} style={{ minWidth: 0 }}>
              <div style={{ background: s.bg, borderRadius: '10px 10px 0 0', padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: `1px solid ${s.color}25` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.color }}></div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: s.color }}>{s.label}</span>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: s.color, background: '#fff', padding: '2px 7px', borderRadius: 10 }}>{allStageDeals.length}</span>
              </div>
              <div style={{ background: '#f8fafc', borderRadius: '0 0 10px 10px', border: `1px solid ${s.color}15`, borderTop: 'none', padding: 6, minHeight: 120, maxHeight: 320, overflowY: 'auto' }}>
                {allStageDeals.map(d => (
                  <DealCard key={d.id} deal={d} stage={s} onMove={handleMove} onDelete={handleDelete} onEdit={setEditDeal} />
                ))}
                {allStageDeals.length === 0 && <div style={{ textAlign: 'center', padding: 16, fontSize: 12, color: '#9ca3af' }}>Kosong</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== ROW 2: 3 Kolom — Ringkasan | Aktivitas | Follow-up ===== */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1fr', gap: 14 }}>
        {/* Kolom 1: Ringkasan Pipeline */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
            📊 Ringkasan Pipeline
          </h4>
          {/* 4 Stat Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
            <div style={{ background: '#eff6ff', borderRadius: 8, padding: '10px 12px', borderLeft: '3px solid #3b82f6' }}>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Total Deal</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}>{totalDealCount}</div>
            </div>
            <div style={{ background: '#ecfdf5', borderRadius: 8, padding: '10px 12px', borderLeft: '3px solid #10b981' }}>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Total Nilai</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#065f46' }}>{formatRupiah(totalNilai)}</div>
            </div>
            <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '10px 12px', borderLeft: '3px solid #059669' }}>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Deal Menang</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#059669' }}>{dealMenangCount}</div>
            </div>
            <div style={{ background: '#fef2f2', borderRadius: 8, padding: '10px 12px', borderLeft: '3px solid #ef4444' }}>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Deal Lose</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#ef4444' }}>{dealLoseCount}</div>
            </div>
          </div>
          {/* Pie Chart — CSS conic-gradient */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 90, height: 90, borderRadius: '50%',
              background: `conic-gradient(#10b981 0% ${pctMenang}%, #ef4444 ${pctMenang}% 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              flexShrink: 0,
            }}>
              <div style={{ width: 50, height: 50, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#374151' }}>
                {totalResult > 0 ? `${pctMenang}%` : '-'}
              </div>
            </div>
            <div style={{ fontSize: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: '#10b981' }}></div>
                <span style={{ color: '#374151' }}>Menang: <b>{pctMenang}%</b> ({dealMenangCount})</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: '#ef4444' }}></div>
                <span style={{ color: '#374151' }}>Lose: <b>{pctLose}%</b> ({dealLoseCount})</span>
              </div>
            </div>
          </div>
        </div>

        {/* Kolom 2: Aktivitas Terbaru */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
            ⚡ Aktivitas Terbaru
          </h4>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>Aktivitas</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>Tipe</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>Klien</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>Deal</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>User</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11 }}>Tanggal</th>
                </tr>
              </thead>
              <tbody>
                {activities.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>Belum ada aktivitas</td></tr>
                ) : activities.map((a, i) => (
                  <tr key={`${a.tipe}-${a.id}-${i}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '6px 8px', color: '#374151', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.aktivitas || '-'}</td>
                    <td style={{ padding: '6px 8px' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
                        background: a.tipe === 'Catatan' ? '#fef3c7' : a.tipe === 'telepon' ? '#dbeafe' : a.tipe === 'email' ? '#ede9fe' : '#f3f4f6',
                        color: a.tipe === 'Catatan' ? '#92400e' : a.tipe === 'telepon' ? '#1e40af' : a.tipe === 'email' ? '#5b21b6' : '#374151'
                      }}>{a.tipe}</span>
                    </td>
                    <td style={{ padding: '6px 8px', color: '#6b7280', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>{a.contact_nama || '-'}</td>
                    <td style={{ padding: '6px 8px', color: '#6b7280', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.deal_judul || '-'}</td>
                    <td style={{ padding: '6px 8px', color: '#6b7280' }}>{a.user_nama || '-'}</td>
                    <td style={{ padding: '6px 8px', color: '#9ca3af', fontSize: 11, whiteSpace: 'nowrap' }}>{a.tanggal?.substring(0, 16) || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Kolom 3: Follow-up Mendatang */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
            📅 Follow-up Mendatang
          </h4>
          {upcomingFollowups.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af', fontSize: 13 }}>
              Tidak ada follow-up mendatang
            </div>
          ) : (
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {upcomingFollowups.map(f => {
                const jadwalDate = new Date(f.jadwal);
                const isHariIni = jadwalDate.toDateString() === new Date().toDateString();
                const isBesok = jadwalDate.toDateString() === new Date(Date.now() + 86400000).toDateString();
                const tipeColors = { telepon: '#3b82f6', email: '#8b5cf6', kunjungan: '#f59e0b', meeting: '#10b981', lainnya: '#6b7280' };
                return (
                  <div key={f.id} style={{ padding: '8px 10px', background: isHariIni ? '#eff6ff' : '#f8fafc', borderRadius: 8, marginBottom: 6, borderLeft: `3px solid ${isHariIni ? '#3b82f6' : isBesok ? '#f59e0b' : '#e5e7eb'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{f.contact_nama}</span>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 4, background: tipeColors[f.tipe] + '20', color: tipeColors[f.tipe] || '#6b7280' }}>{f.tipe}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>{f.judul || f.catatan || '-'}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: isHariIni ? '#3b82f6' : isBesok ? '#f59e0b' : '#9ca3af', fontWeight: isHariIni ? 600 : 400 }}>
                        📅 {f.jadwal?.substring(0, 16)}
                      </span>
                      {f.deal_judul && <span style={{ fontSize: 10, color: '#9ca3af' }}>🎯 {f.deal_judul}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showAdd && <DealModal contacts={contacts} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editDeal && <DealModal contacts={contacts} editData={editDeal} onClose={() => setEditDeal(null)} onSaved={handleEditSave} />}
      {viewDeal && <DealDetailModal deal={viewDeal} onClose={() => setViewDeal(null)} />}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 420 }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#dc2626' }}>🗑️ Hapus Deal</h3>
              <button onClick={() => setShowDeleteModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              {deleteError && (
                <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertTriangle size={14} /> {deleteError}
                </div>
              )}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>Keterangan Penghapusan *</label>
                <textarea value={deleteKeterangan} onChange={e => setDeleteKeterangan(e.target.value)}
                  placeholder="Masukkan alasan penghapusan..."
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, height: 70, resize: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>Password *</label>
                <input type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)}
                  placeholder="Masukkan password hapus"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowDeleteModal(false)}
                  style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, cursor: 'pointer', background: '#fff' }}>Batal</button>
                <button onClick={confirmDelete} disabled={deleteLoading}
                  style={{ padding: '8px 16px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, background: deleteLoading ? '#fca5a5' : '#dc2626', color: '#fff', cursor: deleteLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {deleteLoading ? <><RefreshCw size={14} className="spinner" /> Menghapus...</> : <><Trash2 size={14} /> Hapus</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function DealCard({ deal, stage, onMove, onDelete, onEdit }) {
  const nextIdx = STAGES.indexOf(stage) + 1;
  const nextStage = nextIdx < STAGES.length ? STAGES[nextIdx] : null;
  const [noteCount, setNoteCount] = useState(0);
  const [attachCount, setAttachCount] = useState(0);

  useEffect(() => {
    getCRMDealNotes(deal.id).then(({ data }) => setNoteCount(data.length)).catch(() => {});
    getCRMDealAttachments(deal.id).then(({ data }) => setAttachCount(data.length)).catch(() => {});
  }, [deal.id]);

  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: '10px 12px', marginBottom: 6, boxShadow: '0 1px 2px rgba(0,0,0,0.04)', border: '1px solid #e5e7eb' }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 3 }}>{deal.contact_nama}</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{deal.judul}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{ fontSize: 10, color: '#9ca3af' }}>📅 {deal.created_at?.substring(0, 10)}</span>
        {deal.created_nama && <span style={{ fontSize: 10, color: '#9ca3af' }}>— {deal.created_nama}</span>}
      </div>
      {deal.link && (
        <div style={{ marginBottom: 4 }}>
          <a href={deal.link} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#3b82f6', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}>
            🔗 {deal.link.length > 30 ? deal.link.substring(0, 30) + '...' : deal.link}
          </a>
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        {noteCount > 0 && (
          <span style={{ fontSize: 11, color: '#8b5cf6', display: 'flex', alignItems: 'center', gap: 2 }}>
            <MessageSquare size={11} /> {noteCount}
          </span>
        )}
        {attachCount > 0 && (
          <span style={{ fontSize: 11, color: '#3b82f6', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Paperclip size={11} /> {attachCount}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>{formatRupiah(deal.nilai_estimasi)}</span>
        <div style={{ display: 'flex', gap: 4 }}>
          {stage.key === 'negosiasi' ? (
            <>
              <button onClick={() => onMove(deal.id, 'deal')} title="Deal"
                style={{ display: 'flex', alignItems: 'center', gap: 2, background: '#ecfdf5', border: '1px solid #10b98130', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#059669' }}>
                <CheckCircle size={12} /> Deal
              </button>
              <button onClick={() => onMove(deal.id, 'lose')} title="Lost"
                style={{ display: 'flex', alignItems: 'center', gap: 2, background: '#fef2f2', border: '1px solid #ef444430', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#dc2626' }}>
                <AlertTriangle size={12} /> Lost
              </button>
            </>
          ) : nextStage ? (
            <button onClick={() => onMove(deal.id, nextStage.key)} title={`→ ${nextStage.label}`}
              style={{ background: '#eff6ff', border: 'none', borderRadius: 4, padding: '3px 6px', cursor: 'pointer' }}>
              <ArrowRight size={13} color="#3b82f6" />
            </button>
          ) : null}
          <button onClick={() => onEdit(deal)} title="Edit"
            style={{ background: '#fef3c7', border: 'none', borderRadius: 4, padding: '3px 6px', cursor: 'pointer' }}>
            <Edit3 size={13} color="#f59e0b" />
          </button>
          <button onClick={() => onDelete(deal.id)} title="Hapus" style={{ background: '#fef2f2', border: 'none', borderRadius: 4, padding: '3px 6px', cursor: 'pointer' }}>
            <Trash2 size={13} color="#ef4444" />
          </button>
        </div>
      </div>
    </div>
  );
}

function DealModal({ contacts, editData, onClose, onSaved }) {
  const [form, setForm] = useState({
    contact_id: editData?.contact_id || '',
    judul: editData?.judul || '',
    nilai_estimasi: editData?.nilai_estimasi || '',
    kendaraan_info: editData?.kendaraan_info || '',
    link: editData?.link || '',
    tgl_target_close: editData?.tgl_target_close || '',
    catatan: editData?.catatan || '',
  });
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [uploading, setUploading] = useState(false);
  const isEdit = !!editData;

  useEffect(() => {
    if (editData?.id) {
      getCRMDealAttachments(editData.id).then(({ data }) => setAttachments(data)).catch(() => {});
      getCRMDealNotes(editData.id).then(({ data }) => setNotes(data)).catch(() => {});
    }
  }, [editData?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.contact_id || !form.judul.trim()) return alert('Contact & Judul wajib diisi');
    if (editData) {
      onSaved(form);
    } else {
      try {
        const { data } = await createCRMDeal({ ...form, contact_id: parseInt(form.contact_id), nilai_estimasi: parseFloat(form.nilai_estimasi) || 0 });
        // Upload pending files after deal is created
        if (pendingFiles.length > 0 && data.id) {
          for (const file of pendingFiles) {
            const fd = new FormData();
            fd.append('file', file);
            try { await uploadCRMDealAttachment(data.id, fd); } catch (err) { console.error('Upload gagal:', file.name, err); }
          }
        }
        onSaved();
      } catch (err) { alert('Gagal: ' + err.message); }
    }
  };

  const handlePendingUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setPendingFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const removePendingFile = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const { data } = await uploadCRMDealAttachment(editData.id, fd);
        setAttachments(prev => [{ id: data.id, filename: data.filename, file_url: data.url, created_at: new Date().toISOString() }, ...prev]);
      } catch (err) { alert('Gagal upload: ' + file.name); }
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDeleteAttachment = async (attId) => {
    if (!window.confirm('Hapus file ini?')) return;
    try {
      await deleteCRMDealAttachment(editData.id, attId);
      setAttachments(prev => prev.filter(a => a.id !== attId));
    } catch (err) { alert('Gagal'); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      const { data } = await createCRMDealNote(editData.id, { isi: newNote, is_manager: false });
      setNotes(prev => [...prev, { id: data.id, isi: newNote, user_nama: 'Anda', is_manager: false, created_at: new Date().toISOString() }]);
      setNewNote('');
    } catch (err) { alert('Gagal'); }
  };

  const inputS = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 };
  const labelS = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: isEdit ? 600 : 450, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{isEdit ? 'Edit' : 'Tambah'} Deal</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: 18 }}>
          <div style={{ marginBottom: 12 }}><label style={labelS}>Contact *</label>
            <select style={inputS} value={form.contact_id} onChange={e => setForm({ ...form, contact_id: e.target.value })}>
              <option value="">Pilih...</option>
              {contacts.map(c => <option key={c.id} value={c.id}>{c.nama}</option>)}
            </select></div>
          <div style={{ marginBottom: 12 }}><label style={labelS}>Judul Deal *</label><input style={inputS} value={form.judul} onChange={e => setForm({ ...form, judul: e.target.value })} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div><label style={labelS}>Nilai Estimasi</label><input style={inputS} type="number" value={form.nilai_estimasi} onChange={e => setForm({ ...form, nilai_estimasi: e.target.value })} /></div>
            <div><label style={labelS}>Target Close</label><input style={inputS} type="date" value={form.tgl_target_close} onChange={e => setForm({ ...form, tgl_target_close: e.target.value })} /></div>
          </div>
          <div style={{ marginBottom: 12 }}><label style={labelS}>Link / Tautan</label><input style={inputS} placeholder="https://..." value={form.link} onChange={e => setForm({ ...form, link: e.target.value })} /></div>
          <div style={{ marginBottom: 12 }}><label style={labelS}>Catatan</label><textarea style={{ ...inputS, height: 60 }} value={form.catatan} onChange={e => setForm({ ...form, catatan: e.target.value })} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>Batal</button>
            <button type="submit" style={{ padding: '8px 16px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, background: '#6366f1', color: '#fff', cursor: 'pointer' }}>Simpan</button>
          </div>
        </form>

        {/* Attachments */}
        <div style={{ padding: '0 18px 18px' }}>
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 14, marginTop: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
                📎 Dokumen {isEdit ? `(${attachments.length})` : pendingFiles.length > 0 ? `(${pendingFiles.length} dipilih)` : ''}
              </span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: '#eff6ff', borderRadius: 6, fontSize: 13, color: '#3b82f6', cursor: 'pointer', fontWeight: 600 }}>
                <Upload size={13} /> {uploading ? 'Upload...' : 'Pilih File'}
                <input type="file" multiple onChange={isEdit ? handleUpload : handlePendingUpload} style={{ display: 'none' }} accept='.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif' />
              </label>
            </div>
            {/* Edit mode: show uploaded attachments */}
            {isEdit && attachments.map(a => (
              <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#f8fafc', borderRadius: 6, marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FileText size={13} color="#6b7280" />
                  <span style={{ fontSize: 13, color: '#374151' }}>{a.filename}</span>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <a href={a.file_url} target="_blank" rel="noreferrer" title="Lihat / Download"
                    style={{ background: '#eff6ff', border: 'none', borderRadius: 4, padding: 3, cursor: 'pointer', display: 'flex' }}><Eye size={13} color="#3b82f6" /></a>
                  <button onClick={() => handleDeleteAttachment(a.id)} style={{ background: '#fef2f2', border: 'none', borderRadius: 4, padding: 3, cursor: 'pointer' }}><Trash2 size={12} color="#ef4444" /></button>
                </div>
              </div>
            ))}
            {/* Create mode: show pending files */}
            {!isEdit && pendingFiles.map((f, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#f0f9ff', borderRadius: 6, marginBottom: 4, border: '1px solid #bae6fd' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FileText size={13} color="#3b82f6" />
                  <span style={{ fontSize: 13, color: '#374151' }}>{f.name}</span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>({(f.size / 1024).toFixed(0)} KB)</span>
                </div>
                <button onClick={() => removePendingFile(idx)} style={{ background: '#fef2f2', border: 'none', borderRadius: 4, padding: 3, cursor: 'pointer' }}><Trash2 size={12} color="#ef4444" /></button>
              </div>
            ))}
            {!isEdit && attachments.length === 0 && pendingFiles.length === 0 && (
              <div style={{ fontSize: 13, color: '#9ca3af', padding: '8px 0' }}>Belum ada dokumen</div>
            )}
            {!isEdit && pendingFiles.length > 0 && (
              <div style={{ fontSize: 11, color: '#6b7280', padding: '4px 0', fontStyle: 'italic' }}>* File akan diupload setelah deal disimpan</div>
            )}
          </div>

          {/* Notes — only in edit mode */}
          {isEdit && (
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 14, marginTop: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>💬 Catatan & Komunikasi</div>
              <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 10 }}>
                {notes.length === 0 ? (
                  <div style={{ fontSize: 13, color: '#9ca3af', padding: '4px 0' }}>Belum ada catatan</div>
                ) : notes.map(n => (
                  <div key={n.id} style={{ padding: '8px 10px', background: n.is_manager ? '#fef3c7' : '#eff6ff', borderRadius: 8, marginBottom: 6, borderLeft: `3px solid ${n.is_manager ? '#f59e0b' : '#3b82f6'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: n.is_manager ? '#92400e' : '#1e40af' }}>{n.user_nama} {n.is_manager && '⭐'}</span>
                      <span style={{ fontSize: 11, color: '#9ca3af' }}>{n.created_at}</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{n.isi}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Tambah catatan..."
                  style={{ flex: 1, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, resize: 'none', height: 50 }} />
                <button type="button" onClick={handleAddNote} disabled={!newNote.trim()}
                  style={{ padding: '8px 12px', background: newNote.trim() ? '#6366f1' : '#e5e7eb', color: newNote.trim() ? '#fff' : '#9ca3af', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: newNote.trim() ? 'pointer' : 'not-allowed', alignSelf: 'flex-end' }}>
                  Kirim
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ DEAL DETAIL MODAL ============
function DealDetailModal({ deal, onClose }) {
  const [attachments, setAttachments] = useState([]);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    if (deal?.id) {
      getCRMDealAttachments(deal.id).then(({ data }) => setAttachments(data)).catch(() => {});
      getCRMDealNotes(deal.id).then(({ data }) => setNotes(data)).catch(() => {});
    }
  }, [deal?.id]);

  if (!deal) return null;
  const stageInfo = STAGES.find(s => s.key === deal.stage) || STAGES[0];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 550, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>📋 Detail Deal</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 18 }}>
          {/* Info Utama */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>{deal.contact_nama}</div>
            <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 8 }}>{deal.judul}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 6, background: stageInfo.bg, color: stageInfo.color }}>{stageInfo.label}</span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>📅 {deal.created_at?.substring(0, 16)}</span>
              {deal.created_nama && <span style={{ fontSize: 12, color: '#6b7280' }}>👤 {deal.created_nama}</span>}
            </div>
          </div>

          {/* Detail Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
            {[['Nilai Estimasi', formatRupiah(deal.nilai_estimasi)], ['Kendaraan', deal.kendaraan_info || '-'], ['Target Close', deal.tgl_target_close || '-'], ['Link', deal.link ? '🔗 Ada' : '-']].map(([l, v]) => (
              <div key={l} style={{ padding: '8px 10px', background: '#f8fafc', borderRadius: 6 }}>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{l}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{v}</div>
              </div>
            ))}
          </div>

          {deal.link && (
            <div style={{ marginBottom: 16 }}>
              <a href={deal.link} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: '#3b82f6' }}>🔗 Buka Link</a>
            </div>
          )}

          {deal.catatan && (
            <div style={{ marginBottom: 16, padding: '10px 12px', background: '#fffbeb', borderRadius: 8, borderLeft: '3px solid #f59e0b' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>📝 Catatan</div>
              <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{deal.catatan}</div>
            </div>
          )}

          {/* Dokumen */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>📎 Dokumen ({attachments.length})</div>
            {attachments.length === 0 ? <div style={{ fontSize: 13, color: '#9ca3af' }}>Tidak ada dokumen</div> : attachments.map(a => (
              <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#f8fafc', borderRadius: 6, marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FileText size={13} color="#6b7280" />
                  <span style={{ fontSize: 13 }}>{a.filename}</span>
                </div>
                <a href={a.file_url} target="_blank" rel="noreferrer" title="Lihat / Download"
                  style={{ background: '#eff6ff', border: 'none', borderRadius: 4, padding: 4, cursor: 'pointer', display: 'flex' }}><Eye size={13} color="#3b82f6" /></a>
              </div>
            ))}
          </div>

          {/* Catatan / Komunikasi */}
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>💬 Catatan ({notes.length})</div>
            {notes.length === 0 ? <div style={{ fontSize: 13, color: '#9ca3af' }}>Tidak ada catatan</div> : notes.map(n => (
              <div key={n.id} style={{ padding: '8px 10px', background: n.is_manager ? '#fef3c7' : '#eff6ff', borderRadius: 8, marginBottom: 6, borderLeft: `3px solid ${n.is_manager ? '#f59e0b' : '#3b82f6'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: n.is_manager ? '#92400e' : '#1e40af' }}>{n.user_nama} {n.is_manager && '⭐'}</span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{n.created_at}</span>
                </div>
                <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{n.isi}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ FOLLOWUPS ============
function FollowupsTab() {
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState('today');
  const [showModal, setShowModal] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fRes, cRes, dRes] = await Promise.all([
        getCRMFollowups({ date: filterDate }),
        getCRMContacts(),
        getCRMDeals(),
      ]);
      setFollowups(fRes.data);
      setContacts(cRes.data);
      setDeals(dRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterDate]);

  useEffect(() => { load(); }, [load]);

  const handleComplete = async (id) => {
    try { await completeCRMFollowup(id); load(); } catch (e) { alert('Gagal: ' + e.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus follow-up?')) return;
    try { await deleteCRMFollowup(id); load(); } catch (e) { alert('Gagal: ' + e.message); }
  };

  const handleSave = async (form) => {
    try { await createCRMFollowup(form); setShowModal(false); load(); } catch (e) { alert('Gagal: ' + e.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <select value={filterDate} onChange={e => setFilterDate(e.target.value)}
          style={{ padding: '9px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}>
          <option value="today">📅 Hari Ini</option>
          <option value="week">📆 Minggu Ini</option>
          <option value="overdue">⚠️ Terlewat</option>
          <option value="all">📋 Semua</option>
        </select>
        <button onClick={() => setShowModal(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '9px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          <Plus size={15} /> Tambah
        </button>
      </div>
      <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Jadwal</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Contact</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Tipe</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Judul</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Status</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: 28, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Memuat...</td></tr>
            ) : followups.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 28, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Tidak ada follow-up</td></tr>
            ) : followups.map(f => {
              const overdue = f.status === 'terjadwal' && new Date(f.jadwal) < new Date();
              return (
                <tr key={f.id} style={{ borderBottom: '1px solid #f3f4f6', background: overdue ? '#fef2f2' : undefined }}>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: overdue ? '#ef4444' : '#1e293b' }}>{f.jadwal?.substring(0, 16)}</div>
                    {overdue && <div style={{ fontSize: 12, color: '#ef4444' }}>⚠️ Terlewat</div>}
                  </td>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>{f.contact_nama}</td>
                  <td style={{ padding: '10px 14px' }}>{f.tipe}</td>
                  <td style={{ padding: '10px 14px', color: '#374151' }}>{f.judul || '-'}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
                      background: f.status === 'selesai' ? '#ecfdf5' : '#eff6ff',
                      color: f.status === 'selesai' ? '#065f46' : '#1e40af'
                    }}>{f.status}</span>
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                      {f.status === 'terjadwal' && (
                        <button onClick={() => handleComplete(f.id)} style={{ background: '#ecfdf5', border: 'none', borderRadius: 6, padding: 6, cursor: 'pointer' }}><CheckCircle size={15} color="#10b981" /></button>
                      )}
                      <button onClick={() => handleDelete(f.id)} style={{ background: '#fef2f2', border: 'none', borderRadius: 6, padding: 6, cursor: 'pointer' }}><Trash2 size={15} color="#ef4444" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {showModal && <FollowupModal contacts={contacts} deals={deals} onClose={() => setShowModal(false)} onSave={handleSave} />}
    </div>
  );
}

function FollowupModal({ contacts, deals, onClose, onSave }) {
  const [form, setForm] = useState({ contact_id: '', deal_id: '', tipe: 'telepon', judul: '', catatan: '', jadwal: '' });
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.contact_id || !form.jadwal) return alert('Contact & Jadwal wajib diisi');
    onSave({ ...form, contact_id: parseInt(form.contact_id), deal_id: form.deal_id ? parseInt(form.deal_id) : null });
  };
  const inputS = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 };
  const labelS = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 450 }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Tambah Follow-up</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: 18 }}>
          <div style={{ marginBottom: 12 }}><label style={labelS}>Contact *</label>
            <select style={inputS} value={form.contact_id} onChange={e => setForm({ ...form, contact_id: e.target.value })}>
              <option value="">Pilih...</option>
              {contacts.map(c => <option key={c.id} value={c.id}>{c.nama}</option>)}
            </select></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div><label style={labelS}>Tipe</label>
              <select style={inputS} value={form.tipe} onChange={e => setForm({ ...form, tipe: e.target.value })}>
                {FOLLOWUP_TIPE.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select></div>
            <div><label style={labelS}>Jadwal *</label><input style={inputS} type="datetime-local" value={form.jadwal} onChange={e => setForm({ ...form, jadwal: e.target.value })} /></div>
          </div>
          <div style={{ marginBottom: 12 }}><label style={labelS}>Deal (opsional)</label>
            <select style={inputS} value={form.deal_id} onChange={e => setForm({ ...form, deal_id: e.target.value })}>
              <option value="">Tanpa deal...</option>
              {deals.map(d => <option key={d.id} value={d.id}>{d.judul} ({d.contact_nama})</option>)}
            </select></div>
          <div style={{ marginBottom: 12 }}><label style={labelS}>Judul</label><input style={inputS} value={form.judul} onChange={e => setForm({ ...form, judul: e.target.value })} /></div>
          <div style={{ marginBottom: 12 }}><label style={labelS}>Catatan</label><textarea style={{ ...inputS, height: 60 }} value={form.catatan} onChange={e => setForm({ ...form, catatan: e.target.value })} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>Batal</button>
            <button type="submit" style={{ padding: '8px 16px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, background: '#6366f1', color: '#fff', cursor: 'pointer' }}>Simpan</button>
          </div>
        </form>
      </div>
    </div>
  );
}
